import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { InlineWidget } from 'react-calendly';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ThankYou = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const bookingData = location.state || {};
  const [showUpsell, setShowUpsell] = useState(bookingData.showUpsell || false);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds
  const [loading, setLoading] = useState(false);

  // Countdown timer
  useEffect(() => {
    if (!showUpsell) return;

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showUpsell]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleUpsellAccept = async () => {
    const name = prompt('Confirm your name:');
    const email = prompt('Confirm your email:');
    const phone = prompt('Confirm your phone number:');
    
    if (!name || !email || !phone) return;

    setLoading(true);

    try {
      const bookingData = {
        service_type: 'Pick My Brain Power Session - $99 (SPECIAL OFFER)',
        name,
        email,
        phone,
        company: null,
        message: 'Upgraded from free discovery call',
        preferred_date: null
      };

      await axios.post(`${API}/bookings`, bookingData);

      // Navigate to final thank you
      navigate('/thank-you', {
        state: {
          serviceName: 'Pick My Brain Power Session',
          servicePrice: '$99',
          email: email,
          showUpsell: false,
          isUpgraded: true
        }
      });
    } catch (err) {
      console.error('Booking error:', err);
      alert('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white">
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-md border-b border-gray-700">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_pdf-to-site-6/artifacts/jfbpb7og_Solutions%20Logo%20%282%29.png" 
              alt="Ability Healthcare Solutions" 
              className="h-12 w-auto"
              style={{ 
                mixBlendMode: 'screen'
              }}
            />
            <div className="text-left">
              <div className="text-sm uppercase tracking-wider text-gray-300 font-bold">Turning Challenges</div>
              <div className="text-sm uppercase tracking-wider text-blue-400 font-bold">Into Solutions</div>
            </div>
          </Link>
          <Link
            to="/"
            className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Success Message with Calendly */}
        {!showUpsell && bookingData.serviceName === 'Free Discovery Call' && (
          <div>
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl p-6 text-center border-2 border-green-500 mb-8">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl font-black mb-3">Great! Now Schedule Your Call</h1>
              <p className="text-gray-300">Pick a time that works best for you below</p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-2xl p-4">
              <InlineWidget
                url="https://calendly.com/admin-abilityhealthsolutions/30min"
                styles={{
                  height: '700px',
                  width: '100%'
                }}
                prefill={{
                  email: bookingData.email || '',
                  name: bookingData.userName || ''
                }}
              />
            </div>
          </div>
        )}

        {/* Regular Thank You for other services */}
        {!showUpsell && bookingData.serviceName !== 'Free Discovery Call' && (
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl p-8 md:p-12 text-center border-2 border-green-500" data-testid="thank-you-card">
            <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-black mb-4">
              {bookingData.isUpgraded ? 'Congratulations!' : 'Thank You!'}
            </h1>
            
            {bookingData.serviceName && (
              <p className="text-xl md:text-2xl text-gray-300 mb-6">
                {bookingData.isUpgraded ? (
                  <>
                    You've successfully upgraded to <strong className="text-blue-400">{bookingData.serviceName}</strong>
                  </>
                ) : (
                  <>
                    Your request for <strong className="text-blue-400">{bookingData.serviceName}</strong> has been received
                  </>
                )}
              </p>
            )}
            
            <div className="bg-blue-900/30 border-2 border-blue-500 rounded-xl p-8 mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">What Happens Next?</h2>
              <div className="space-y-6 text-left max-w-2xl mx-auto">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">1</div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Check Your Email</h3>
                    <p className="text-gray-300">Confirmation email sent to <strong className="text-blue-400">{bookingData.email || 'your email'}</strong></p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">2</div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">We'll Contact You Within 24 Hours</h3>
                    <p className="text-gray-300">Our team will reach out to schedule your session</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">3</div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Get Ready for Results</h3>
                    <p className="text-gray-300">Prepare your questions and get ready to transform your agency</p>
                  </div>
                </div>
              </div>
            </div>

            <Link
              to="/"
              className="inline-block bg-gradient-to-r from-blue-600 to-green-600 text-white px-10 py-4 rounded-full font-bold hover:from-blue-700 hover:to-green-700 transition-all shadow-xl uppercase tracking-wide"
              data-testid="return-home-btn"
            >
              Return to Home
            </Link>
          </div>
        )}

        {/* Upsell with Countdown Timer */}
        {showUpsell && (
          <div data-testid="upsell-section">
            {/* Success Message First */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl p-8 text-center border-2 border-green-500 mb-8">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold mb-2">Your Free Discovery Call is Booked!</h1>
              <p className="text-gray-300">Confirmation email sent to <strong className="text-blue-400">{bookingData.email}</strong></p>
            </div>

            {/* Countdown Upsell Offer */}
            <div className="bg-gradient-to-br from-blue-900 to-green-900 rounded-2xl shadow-2xl p-8 md:p-12 border-2 border-yellow-400 relative overflow-hidden">
              {/* Countdown Timer */}
              <div className="absolute top-4 right-4 bg-red-600 text-white px-6 py-3 rounded-full font-black text-2xl animate-pulse">
                {formatTime(timeLeft)}
              </div>

              <div className="max-w-3xl mx-auto">
                <div className="text-center mb-8">
                  <span className="inline-block bg-yellow-400 text-black px-6 py-2 rounded-full text-sm font-black uppercase mb-4">Limited Time Offer</span>
                  <h2 className="text-4xl md:text-5xl font-black mb-4">
                    Wait! Special One-Time Offer
                  </h2>
                  <p className="text-2xl text-blue-100 mb-6">
                    Since you just booked, upgrade to a <strong>30-Minute Power Session</strong> for only <span className="text-yellow-400 font-bold">$99</span>
                  </p>
                </div>

                <div className="bg-black/30 backdrop-blur rounded-xl p-8 mb-8">
                  <h3 className="text-2xl font-bold mb-4 text-center">Pick My Brain Power Session</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="text-center">
                      <div className="text-5xl font-black text-red-400 line-through mb-2">$199</div>
                      <div className="text-sm text-gray-400">Regular Price</div>
                    </div>
                    <div className="text-center">
                      <div className="text-6xl font-black text-yellow-400 mb-2">$99</div>
                      <div className="text-sm text-gray-300 font-bold">TODAY ONLY</div>
                    </div>
                  </div>

                  <div className="space-y-3 mb-8">
                    <div className="flex items-center text-lg">
                      <span className="text-green-400 mr-3 text-2xl">✓</span>
                      <span>30 minutes of laser-focused consulting</span>
                    </div>
                    <div className="flex items-center text-lg">
                      <span className="text-green-400 mr-3 text-2xl">✓</span>
                      <span>Solve your biggest challenge right now</span>
                    </div>
                    <div className="flex items-center text-lg">
                      <span className="text-green-400 mr-3 text-2xl">✓</span>
                      <span>Expert strategies proven with 10,000+ clients</span>
                    </div>
                    <div className="flex items-center text-lg">
                      <span className="text-green-400 mr-3 text-2xl">✓</span>
                      <span>Actionable steps you can implement immediately</span>
                    </div>
                  </div>

                  <div className="bg-yellow-400/10 border-2 border-yellow-400 rounded-lg p-4 mb-6">
                    <p className="text-center font-bold text-yellow-400">
                      ⚠️ This offer expires in {formatTime(timeLeft)} or when you leave this page!
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-4 mb-6">
                    <h4 className="text-gray-900 font-bold text-center mb-4">Schedule Your Power Session Now:</h4>
                    <InlineWidget
                      url="https://calendly.com/admin-abilityhealthsolutions/pick-my-brain"
                      styles={{
                        height: '600px',
                        width: '100%'
                      }}
                      prefill={{
                        email: bookingData.email || '',
                        name: bookingData.userName || ''
                      }}
                    />
                  </div>
                </div>

                <div className="text-center">
                  <button
                    onClick={() => setShowUpsell(false)}
                    className="text-gray-400 hover:text-white underline transition-colors"
                    data-testid="decline-upsell-btn"
                  >
                    No thanks, I'll stick with the free call
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThankYou;